/**
 * SessionReplayCard — StreamX Sprint 3a.
 *
 * Operator-facing toggle that wires the PostHog `posthog_init.js`
 * session-replay opt-in/opt-out helpers to a calm, privacy-first UX
 * in the Settings page.
 *
 * Doctrine
 * ────────
 *   • Privacy language first.  Operator must understand exactly what
 *     replay does + what's masked + how to revoke.
 *   • Persistence is immediate — localStorage write happens inside the
 *     posthog SDK, so refreshing preserves the choice.
 *   • Status diagnostic — shows replay env config so operators can
 *     verify it's wired correctly.
 *   • Default OFF — until the operator explicitly opts in, replay
 *     never boots.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Eye, EyeOff, Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { STREAMX_BRAND } from '@/lib/brand';

const STORAGE_KEY = 'op_session_replay_opt_in';

export default function SessionReplayCard() {
  const [enabled, setEnabled] = useState(false);
  const [status, setStatus] = useState({
    configured: false,
    replay_env_enabled: false,
    currently_recording: false,
    privacy_mode: 'replay_off',
  });

  // Initial state from localStorage + posthog status diagnostic
  useEffect(() => {
    try {
      const persisted = localStorage.getItem(STORAGE_KEY);
      if (persisted === 'true') setEnabled(true);
    } catch (_) { /* ignore */ }

    // Pull status diagnostic from posthog_init (lazy import so we never
    // fail if posthog hasn't booted yet)
    import('@/lib/posthog_init')
      .then((m) => { try { setStatus(m.getReplayStatus()); } catch (_) {} })
      .catch(() => { /* posthog optional */ });
  }, []);

  const handleToggle = async (next) => {
    setEnabled(next);
    try { localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false'); } catch (_) {}

    try {
      const mod = await import('@/lib/posthog_init');
      if (next) {
        mod.optInToReplay();
        toast.success('Session replay analytics enabled — thank you for helping us improve.');
      } else {
        mod.optOutOfReplay();
        toast.success('Session replay analytics disabled.');
      }
      try { setStatus(mod.getReplayStatus()); } catch (_) {}
    } catch (_) {
      // posthog SDK not loaded — preference still saved
      toast.info(next ? 'Preference saved — replay will activate when configured.' : 'Preference saved.');
    }
  };

  // ── Status helpers ───────────────────────────────────────────────
  const replayConfigured = status.configured;
  const replayEnvOn = status.replay_env_enabled;
  const isLiveRecording = status.currently_recording;

  return (
    <Card className="max-w-2xl" data-testid="session-replay-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Help Improve {STREAMX_BRAND} Experience
        </CardTitle>
        <CardDescription>
          Allow anonymous session replay analytics to help us improve streaming
          and academy workflows. You can revoke consent any time.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Toggle */}
        <div
          className="flex items-start justify-between gap-4 p-4 rounded-lg border"
          style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--muted) / 0.3)' }}
        >
          <div className="space-y-1 flex-1 min-w-0">
            <Label htmlFor="session-replay-toggle" className="text-sm font-bold cursor-pointer">
              Enable session replay
            </Label>
            <p className="text-xs text-muted-foreground leading-relaxed">
              When enabled, anonymized recordings of YOUR operator surfaces help
              us debug issues, refine workflows, and improve {STREAMX_BRAND}.
              Player-facing surfaces, payments, OTPs, chats, student data, and
              Razorpay forms are <strong>always masked</strong>.
            </p>
          </div>
          <Switch
            id="session-replay-toggle"
            checked={enabled}
            onCheckedChange={handleToggle}
            data-testid="session-replay-toggle"
          />
        </div>

        {/* Privacy guarantees */}
        <div className="space-y-2.5" data-testid="session-replay-privacy-list">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
            Built-in privacy guarantees
          </div>
          <PrivacyRow icon={Lock} label="All passwords, OTPs, and Aadhaar/PAN inputs are masked." />
          <PrivacyRow icon={Lock} label="Razorpay checkout iframe is fully blocked from capture." />
          <PrivacyRow icon={Lock} label="Chat messages and student profile sections are masked." />
          <PrivacyRow icon={Lock} label="Player-facing pages are NEVER recorded — operator surfaces only." />
          <PrivacyRow icon={ShieldCheck} label="Do-Not-Track browser headers are always respected." />
        </div>

        {/* Status diagnostic */}
        <div
          className="text-xs flex flex-wrap items-center gap-2 pt-3 border-t"
          style={{ borderColor: 'hsl(var(--border))' }}
          data-testid="session-replay-status"
        >
          <span className="text-muted-foreground">Status:</span>
          {!replayConfigured ? (
            <Badge variant="outline" className="font-mono text-[10px]" data-testid="session-replay-status-unconfigured">
              <EyeOff className="h-3 w-3 mr-1" />
              Not configured (admin sets POSTHOG_KEY)
            </Badge>
          ) : isLiveRecording ? (
            <Badge className="font-mono text-[10px] bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid="session-replay-status-recording">
              <Eye className="h-3 w-3 mr-1" />
              Recording
            </Badge>
          ) : replayEnvOn && enabled ? (
            <Badge variant="outline" className="font-mono text-[10px]" data-testid="session-replay-status-armed">
              <Eye className="h-3 w-3 mr-1" />
              Armed (will record on next page nav)
            </Badge>
          ) : (
            <Badge variant="outline" className="font-mono text-[10px]" data-testid="session-replay-status-off">
              <EyeOff className="h-3 w-3 mr-1" />
              Disabled
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PrivacyRow({ icon: Icon, label }) {
  return (
    <div className="flex items-start gap-2 text-xs leading-relaxed">
      <Icon className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}
